import { z } from "zod";
import { overrideSchema } from "./override";
import { SandboxTuningConfigSchema } from "./sandbox";

/**
 * The yeet pipeline's remote-tunable namespaces (config-yeet, config-yeetAttempt,
 * config-yeetDispatcher, config-yeetScoring, config-yeetCanonicalize), split out
 * of the registry module by the file-size cap. Same contract as every namespace
 * schema: `.strict()`, a `.default(...)` per field as the no-flag baseline, and
 * loud parse failure on malformed remote payloads.
 */

export const YeetConfigSchema = SandboxTuningConfigSchema.extend({
  agentModel: z.string().default("openai/gpt-6-luna"),
  maxInputTokens: z.number().int().positive().default(3_500_000),
  maxOutputTokens: z.number().int().positive().default(16_384),
  maxSteps: z.number().int().positive().default(64),
  textWindowChars: z.number().int().positive().default(12_000),
  evidencePageSize: z.number().int().positive().default(50),
  // Loop guard. A run of this many consecutive steps that make the identical
  // tool call(s) stops the loop; the model is told once, after the second.
  identicalToolCallLimit: z.number().int().min(2).default(3),
  // Times a text-only ending under required tool choice is re-prompted.
  textOnlyReprompts: z.number().int().min(0).default(1),
  // Broker delivery bound per demo channel. Durable fleet and per-demo leases
  // independently enforce work and sandbox capacity across all replicas.
  prefetch: z.number().int().positive().default(2),
}).strict();
export type YeetConfig = z.infer<typeof YeetConfigSchema>;

// Per-run override of the AppConfig-resolved yeet config, stored on the crawl run
// and merged over the resolved namespace at publish time. Absent = production
// behaviour, byte-identical to no override at all.
export const YeetConfigOverrideSchema = overrideSchema(YeetConfigSchema);
export type YeetConfigOverride = z.infer<typeof YeetConfigOverrideSchema>;

// Sandbox attempt budget, resolved by the backend (the deadline owner).
// The dispatcher heartbeats POST /internal/yeet/attempts/:id/extend; each
// heartbeat slides deadlineAt to now + extendWindowMs, hard-capped at
// createdAt + maxBudgetMs. Keep maxBudgetMs under E2B's 1h continuous-runtime
// ceiling with margin.
export const YeetAttemptConfigSchema = z
  .object({
    initialBudgetMs: z
      .number()
      .int()
      .positive()
      .default(30 * 60_000),
    maxBudgetMs: z
      .number()
      .int()
      .positive()
      .default(55 * 60_000),
    extendWindowMs: z
      .number()
      .int()
      .positive()
      .default(10 * 60_000),
    // A page gets at most this many sandbox attempts before its frontier row
    // goes terminal (attempts start at 1 on admit, so 2 = one sweeper re-admit).
    // The sweeper resolves it directly (backend-side; it is the only publisher);
    // kept low because every yeet attempt costs a sandbox boot plus an LLM loop.
    maxPageAttempts: z.number().int().positive().default(2),
    // Age past which a claimed-but-never-attempted analysis dispatch (stamped
    // analysisDispatchedAt, frontier still in_flight, no attempt) is treated as
    // lost — the backend crashed between claiming and publishing — and the
    // sweeper republishes yeet.page.requested.
    dispatchRecoveryMs: z.number().int().positive().default(120_000),
    // Per-tick work bounds. Both sweeps are unbounded reads otherwise, so a
    // backlog would load every overdue row into one tick.
    sweepBatchSize: z.number().int().positive().default(100),
    dispatchRecoveryBatchSize: z.number().int().positive().default(10),
  })
  .strict();
export type YeetAttemptConfig = z.infer<typeof YeetAttemptConfigSchema>;

// Dispatcher boot-time knobs, resolved by the backend and fetched once by the
// worker at startup (GET /internal/yeet/dispatcher-config) before it consumes
// any job — the worker never reads the flag itself. Governs the fail-loud MCP
// reachability preflight (workers/yeet/src/preflight.ts).
export const YeetDispatcherConfigSchema = z
  .object({
    // Timeout for the boot preflight round-trip to the public MCP `/health`
    // endpoint; past it the dispatcher refuses to boot rather than run against
    // a public callback route that cannot reach the backend.
    preflightTimeoutMs: z.number().int().positive().default(5_000),
    sandboxPreflightTimeoutMs: z.number().int().positive().default(120_000),
  })
  .strict();
export type YeetDispatcherConfig = z.infer<typeof YeetDispatcherConfigSchema>;

// Backend-resolved yeet scoring/briefing caps. Unlike `yeet`, these are NOT
// pinned onto a queue message: they shape backend-side scoring — the get_tov
// prompt digest, the dynamic voice_trust checklist, and issue consolidation —
// and are resolved via ConfigResolver at the MCP-request / materialize boundary,
// never read worker-side.
export const YeetScoringConfigSchema = z
  .object({
    // Cap on dynamic voice_trust checks derived from a client's TOV snapshot, so
    // a rule-heavy client cannot make its voice_trust score mostly-TOV while
    // others score mostly-static (cross-client comparability).
    dynamicCheckCap: z.number().int().positive().default(10),
    // Character budget for the get_tov prompt digest; past it the digest is cut
    // mid-render with a loud marker (pgvector retrieval is the escape hatch).
    digestCharBudget: z.number().int().positive().default(6_000),
    // Cap on the website-wide problem vocabulary served in briefings (most
    // recently seen first): open problems for get_page_context, all-status
    // known problems for get_canonicalization_briefing.
    problemVocabularyCap: z.number().int().positive().default(100),
  })
  .strict();
export type YeetScoringConfig = z.infer<typeof YeetScoringConfigSchema>;

// Run-level problem-key canonicalization: pinned onto yeet.canonicalize.requested
// at publish time and consumed by the dispatcher's canonicalize handler; the
// timeout/cap pair also drives the backend's backstop sweeper.
export const YeetCanonicalizeConfigSchema = z
  .object({
    // Must hold exact full coverage on large runs: it returns one mapping per
    // briefed pair (70+ on a full-site scan). gpt-4o-mini reliably dropped
    // pairs at that size; kimi-k2 verified 74/74.
    model: z.string().default("moonshotai/kimi-k2-0905"),
    // Corrective LLM retries after a rejected mapping (422) before the worker
    // fails the run.
    maxRetries: z.number().int().min(0).default(2),
    // Output-token cap on one mapping call. Without a cap the provider route
    // chooses its own maximum and can reject the request outright (a Novita
    // route refused kimi-k2 with "max_tokens: 100352 exceeds maximum 98304").
    // A full-site mapping is a few thousand tokens; the cap leaves room for a
    // reasoning model's thinking tokens, which count against it.
    maxOutputTokens: z.number().int().positive().default(16_384),
    // Most briefed pairs one mapping call receives. A larger remainder is
    // split into per-subject calls, and a call that stops at the output cap
    // is split again, so a reasoning model's thinking never consumes the
    // whole budget of a full-site mapping.
    maxPairsPerCall: z.number().int().positive().default(40),
    // Concurrent canonicalize jobs per dispatcher (QoS on the dedicated
    // canonicalize channel, separate from the sandbox channel so mapping jobs
    // are never starved behind hour-long page attempts). Applied from the
    // message like yeet.prefetch.
    prefetch: z.number().int().positive().default(1),
    // Claim age past which the backend sweeper considers the request lost
    // (worker died, queue purged by a deploy) and re-publishes.
    requestTimeoutMs: z
      .number()
      .int()
      .positive()
      .default(10 * 60_000),
    // Total publishes (first + sweeper re-publishes) before the sweeper fails
    // the run instead of retrying forever.
    maxRequests: z.number().int().positive().default(3),
    // Ceiling on distinct (subject, proposedKey) pairs in one briefing; a run
    // past it is a modeling failure and refuses to canonicalize (truncating
    // the mapping input would 422 forever).
    maxProposalPairs: z.number().int().positive().default(200),
    // Sample titles / recommended actions included per proposal pair.
    samplesPerPair: z.number().int().positive().default(5),
  })
  .strict();
export type YeetCanonicalizeConfig = z.infer<
  typeof YeetCanonicalizeConfigSchema
>;
